/**
 * @fileoverview Input/output schemas for world-place-ideation-v3.
 *
 * This agent generates ideas for significant places (locations, buildings).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { WorldPlaceIdeaV3Schema, WorldPlaceElementEnum } from '@/domains/world-building/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldPlaces.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.initialChapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  targetElement: WorldPlaceElementEnum.optional().describe(
    'Optional filter to generate only a specific element type (location or building). If not provided, generates a mix of both.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema without tempId and source (added by the hook).
 */
const WorldPlaceIdeaOutputSchema = WorldPlaceIdeaV3Schema.omit({
  tempId: true,
  source: true,
});

export const OutputSchema = z.object({
  places: z.array(WorldPlaceIdeaOutputSchema).describe('An array of generated world place ideas.'),
});

export type Output = z.infer<typeof OutputSchema>;
